/**
 * Transforms API yAxis format to ECharts yAxis configuration.
 *
 * API format: [{ dataType: "number", dataValue: 0 }, { dataType: "number", dataValue: 17500 }, ...]
 * ECharts format: { type: 'value', min: 0, max: 70000, interval: 17500 }
 *
 * Features:
 * - Always includes 0 as baseline (unless data has negative values)
 * - Uses interval from API data
 * - Extends below 0 if chart data contains negative values (rounded to nearest interval)
 */

interface ApiYAxisItem {
  dataType: string;
  dataValue: number;
}

interface BarComponent {
  yValue: string | number;
  xValue?: string;
  label?: string;
  groupName?: string;
}

interface Bar {
  barName?: string;
  barComponent?: BarComponent[];
}

interface BarChartDetail {
  yAxis?: ApiYAxisItem[];
  body?: {
    bars?: Bar[];
  };
}

interface EChartsYAxisConfig {
  type: 'value';
  min: number;
  max: number;
  interval?: number;
}

/**
 * Extracts all numeric values from bar chart data
 */
const extractAllDataValues = (bars: Bar[]): number[] => {
  return bars.flatMap(
    (bar) =>
      bar.barComponent?.map((comp) => {
        const val = typeof comp.yValue === 'string' ? parseFloat(comp.yValue) : comp.yValue;
        return isNaN(val) ? 0 : val;
      }) || []
  );
};

/**
 * Calculates the minimum value for yAxis, extending below 0 if needed
 * Rounds down to the nearest interval for clean axis labels
 */
const calculateMinValue = (
  bars: Bar[] | undefined,
  interval: number | undefined
): number => {
  if (!bars || !Array.isArray(bars) || !interval) {
    return 0;
  }

  const allDataValues = extractAllDataValues(bars);
  const minDataValue = allDataValues.length > 0 ? Math.min(...allDataValues) : 0;

  // If there are negative values, extend min below 0 (round down to nearest interval)
  if (minDataValue < 0) {
    return Math.floor(minDataValue / interval) * interval;
  }

  return 0;
};

/**
 * Transforms API bar chart detail object to ECharts yAxis configuration.
 *
 * @param barChartDetail - The bar chart detail object from API containing yAxis and body.bars
 * @returns ECharts yAxis configuration object or null if no valid data
 *
 * @example
 * const barChartDetail = {
 *   yAxis: [
 *     { dataType: "number", dataValue: 0 },
 *     { dataType: "number", dataValue: 17500 },
 *     { dataType: "number", dataValue: 70000 }
 *   ],
 *   body: {
 *     bars: [{ barComponent: [{ yValue: "15000" }, { yValue: "-5000" }] }]
 *   }
 * };
 *
 * const yAxisConfig = transformApiYAxisToECharts(barChartDetail);
 * // Returns: { type: 'value', min: -17500, max: 70000, interval: 17500 }
 */
export const transformApiYAxisToECharts = (
  barChartDetail: BarChartDetail | null | undefined
): EChartsYAxisConfig | null => {
  const apiYAxis = barChartDetail?.yAxis;

  if (!apiYAxis || !Array.isArray(apiYAxis) || apiYAxis.length === 0) {
    return null;
  }

  // Extract values from API format
  const yAxisValues = apiYAxis.map((item) => item.dataValue);
  const maxValue = Math.max(...yAxisValues);
  const interval = yAxisValues.length > 1 ? yAxisValues[1] - yAxisValues[0] : undefined;

  // Calculate min value (0 by default, but extends below if data has negative values)
  const minValue = calculateMinValue(barChartDetail?.body?.bars, interval);

  return {
    type: 'value',
    min: minValue,
    max: maxValue,
    interval: interval,
  };
};

export default transformApiYAxisToECharts;
